/**
 * Search / browse. Filters live in URL search params so a "See all →" from Home can land
 * pre-filtered, and so back/forward restores the exact result set.
 */

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SOURCE_PLATFORMS, type PromptCard as PromptCardDto, type PromptType } from '@promptvault/shared'; // prettier-ignore
import { fetchFeed, fetchSearch, toggleFavorite, type FeedSort } from '../api/prompts';
import { fetchTaxonomy } from '../api/taxonomy';
import { BottomSheet, SheetOptionGroup } from '../components/BottomSheet';
import { PromptCard } from '../components/PromptCard';
import { Padded, Screen } from '../components/Screen';
import { Chip, ChipRow, SearchBar } from '../components/controls';
import { ErrorState, Placeholder, SkeletonGrid } from '../components/feedback';
import { Button } from '../components/primitives';
import { compactNumber, withCount } from '../lib/format';
import { promptTypeLabel, useI18n } from '../lib/i18n';
import { haptic } from '../hooks/useTg';
import { useDebounced } from '../hooks/useDebounced';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import { useMe } from '../providers/AppProviders';

/**
 * "Popular at source" sits second, straight after Trending.
 *
 * For this library it is the most useful ordering there is — our own download and rating counters
 * are near zero across nearly every row, so `most_downloaded` and `top_rated` are close to
 * arbitrary, while the source's numbers are real. Ranking the *options* by how much signal they
 * carry is part of the design, not an accident of array order.
 */
const SORTS: FeedSort[] = ['trending', 'source_popular', 'new', 'top_rated', 'most_downloaded'];

/**
 * Display names for the filter chips, matching what a card shows.
 *
 * Duplicated from the server's map rather than fetched, because a filter list is chrome that must
 * render before any request resolves — and if the two ever disagree the symptom is a chip reading
 * "civitai.com" above cards reading "Civitai", which looks like two different filters.
 */
const PLATFORM_LABELS: Record<(typeof SOURCE_PLATFORMS)[number], string> = {
  'civitai.com': 'Civitai',
  'meigen.ai': 'meigen',
  'youmind.com': 'youmind',
  'promptden.com': 'PromptDen',
  'github.com': 'GitHub',
};

export function Search() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { t, lang } = useI18n();
  const me = useMe();

  const [sheet, setSheet] = useState(false);
  const [q, setQ] = useState(params.get('q') ?? '');
  const debouncedQ = useDebounced(q, 300);
  /** Set by "search instead for …", cleared by the next query. See the correction line below. */
  const [exact, setExact] = useState(false);

  const type = (params.get('type') as PromptType | null) ?? undefined;
  const model = params.get('model') ?? undefined;
  const category = params.get('category') ?? undefined;
  const tag = params.get('tag') ?? undefined;
  const sort = (params.get('sort') as FeedSort | null) ?? 'trending';
  const platform = params.get('platform') ?? undefined;

  const setParam = useCallback(
    (key: string, value?: string) => {
      const next = new URLSearchParams(params);
      if (value) next.set(key, value);
      else next.delete(key);
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  const { data: taxonomy } = useQuery({
    queryKey: ['taxonomy'],
    queryFn: fetchTaxonomy,
    staleTime: 3_600_000,
  });

  const searching = debouncedQ.trim().length >= 2;

  /**
   * The query text belongs in the URL, like every other filter on this screen.
   *
   * The header of this file has always claimed that filters live in search params "so back/
   * forward restores the exact result set". The type, model, category, tag and sort did. `q` was
   * read from the URL once at mount and never written back, so searching, opening a result and
   * pressing Back returned an empty box and the unfiltered feed — losing the one piece of state
   * the user had typed themselves.
   *
   * Synced from the *debounced* value, so this is one history entry per search rather than one
   * per keystroke, and `replace` so Back leaves the screen instead of walking the edit history.
   *
   * `focus` is dropped at the same time: it is a launch intent from the search affordances, and
   * once a query exists it has been served. Leaving it would re-raise the keyboard on every
   * Back — the bug the `autoFocus` comment below was written about. It survives an empty `q`,
   * because this effect returns before touching anything when nothing changed.
   */
  useEffect(() => {
    const current = params.get('q') ?? '';
    const next = debouncedQ.trim();
    if (current === next) return;
    const updated = new URLSearchParams(params);
    if (next) updated.set('q', next);
    else updated.delete('q');
    updated.delete('focus');
    setParams(updated, { replace: true });
  }, [debouncedQ, params, setParams]);

  // A new query gets the spelling correction again; `exact` is an answer to one specific offer.
  useEffect(() => setExact(false), [debouncedQ]);

  const feed = useInfiniteQuery({
    queryKey: ['feed', sort, type, model, category, tag, platform, me?.settings.nsfwVisible],
    queryFn: ({ pageParam }) =>
      fetchFeed({
        sort,
        ...(platform ? { platform } : {}),
        type,
        model,
        category,
        tag,
        nsfw: me?.settings.nsfwVisible,
        cursor: pageParam as string | undefined,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: !searching,
  });

  const search = useInfiniteQuery({
    /**
     * `nsfwVisible` belongs in this key for the same reason it is in the feed's, and `exact`
     * because it is a different question with a different answer. Without the first, toggling
     * NSFW left the previous search's results cached and on screen; without the second, "search
     * instead for what I typed" would read the corrected search back out of the cache.
     */
    queryKey: ['search', debouncedQ, exact, me?.settings.nsfwVisible],
    queryFn: ({ pageParam }) =>
      fetchSearch(debouncedQ.trim(), pageParam as string | undefined, exact),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: searching,
  });

  const active = searching ? search : feed;
  const items = useMemo(() => active.data?.pages.flatMap((p) => p.items) ?? [], [active.data]);
  /** Reported by the first page only — paging does not re-correct. */
  const correction = searching && !exact ? search.data?.pages[0]?.correction : undefined;

  const favMut = useMutation({
    mutationFn: (card: PromptCardDto) => toggleFavorite(card.id),
    onSuccess: () => {
      haptic('medium');
      void qc.invalidateQueries({ queryKey: ['favorites'] });
    },
  });

  const sentinel = useInfiniteScroll(() => void active.fetchNextPage(), {
    enabled: Boolean(active.hasNextPage),
    loading: active.isFetchingNextPage,
  });

  const openCard = useCallback((card: PromptCardDto) => navigate(`/prompt/${card.id}`), [navigate]);

  const filtersActive =
    Boolean(type || model || category || tag || platform) || sort !== 'trending';

  const resetFilters = useCallback(() => {
    const next = new URLSearchParams();
    if (q) next.set('q', q);
    setParams(next, { replace: true });
  }, [q, setParams]);

  const activeCategory = useMemo(
    () => taxonomy?.categories.find((c) => c.slug === category),
    [taxonomy, category],
  );
  const categoryName = activeCategory?.name;
  /**
   * The category's real size, when a category is the only thing narrowing the list.
   *
   * With another filter on top — a type, a model, a tag — the category total is no longer the
   * total of what is on screen, so it is deliberately not offered. A number that is right most
   * of the time is the kind that gets believed when it is wrong.
   */
  const categoryTotal =
    activeCategory && !type && !model && !tag && !platform ? activeCategory.count : undefined;
  const modelName = useMemo(
    () => taxonomy?.models.find((m) => m.slug === model)?.name,
    [taxonomy, model],
  );

  /**
   * One count, rendered by the header line and by the sheet's footer button.
   *
   * D56: a number on screen is either the truth or must not look like a total. The header was
   * fixed for that and the sheet's "Show N results" button was not, so the same screen could
   * print a real category total in one place and a page-sized `items.length` in the other.
   */
  const shownCount =
    categoryTotal !== undefined
      ? compactNumber(categoryTotal, lang)
      : `${compactNumber(items.length, lang)}${active.hasNextPage ? '+' : ''}`;

  const sortLabels: Record<FeedSort, string> = {
    trending: t.trendingSort,
    new: t.newestSort,
    top_rated: t.topSort,
    most_downloaded: t.dlSort,
    source_popular: t.sortSourcePopular,
  };

  return (
    <Screen root chrome="surface">
      {/* Sticky search + filters — the only chrome on this screen. */}
      <div className="hairline-b sticky top-0 z-10 bg-surface px-gutter pb-3 pt-2">
        <SearchBar
          value={q}
          onChange={setQ}
          placeholder={t.searchPh}
          /*
            Focus only when the user asked to search.

            This used to read `!params.get('type') && !params.get('category')` — inferring intent
            from the *shape* of the URL. A rail's "See all" navigates to `/search?sort=trending`,
            which has neither param, so the field focused and the phone keyboard covered half the
            screen on every section tap. Category chips pass `?category=…` and were unaffected,
            which is exactly the asymmetry the owner reported.

            Intent is not a URL shape. `?focus=1` is set by the search affordances and by nothing
            else, so browsing never raises the keyboard.
          */
          autoFocus={params.get('focus') === '1'}
        />
        <div className="pt-3">
          <ChipRow>
            <Chip
              label={type ? promptTypeLabel(type, lang) : t.type}
              selected={Boolean(type)}
              chevron
              onClick={() => setSheet(true)}
            />
            <Chip
              label={modelName ?? t.model}
              selected={Boolean(model)}
              chevron
              onClick={() => setSheet(true)}
            />
            <Chip
              label={categoryName ?? t.category}
              selected={Boolean(category)}
              chevron
              onClick={() => setSheet(true)}
            />
            <Chip
              label={sortLabels[sort]}
              selected={sort !== 'trending'}
              chevron
              onClick={() => setSheet(true)}
            />
          </ChipRow>
        </div>
      </div>

      {tag && (
        <Padded className="pt-3">
          <Chip label={`#${tag}`} selected onClick={() => setParam('tag', undefined)} />
        </Padded>
      )}

      {/*
        The correction, said out loud.

        `/search` corrects a misspelling on the zero-result path and reports what it did — that
        is why `anmie` returns anime prompts instead of nothing. The client discarded the field,
        so the query was silently rewritten and the user was told nothing, which is precisely
        what D64 ruled out: "Never silent … so a UI can say 'showing results for portrait' and
        still offer the exact term."

        Both halves are here. The corrected term is what the results are for, and the original is
        one tap away through `?exact=1`, which is the API's own way of searching a string as
        typed. Each term sits in a `<bdi>`: a Latin word inside a Persian sentence needs an
        isolate or the neutrals around it reorder.
      */}
      {correction && (
        <Padded className="pt-3">
          <div className="flex flex-col items-start gap-1 rounded-card bg-fill px-3.5 py-2.5">
            <p className="text-footnote text-hint">
              {t.showingResultsFor}{' '}
              <bdi className="font-semibold text-text">{correction.corrected}</bdi>
            </p>
            <button
              type="button"
              onClick={() => {
                haptic('light');
                setExact(true);
              }}
              className="text-footnote font-medium text-accent"
            >
              {t.searchInsteadPrefix} <bdi>{correction.original}</bdi>
            </button>
          </div>
        </Padded>
      )}

      {active.isLoading ? (
        <Padded className="pt-3">
          <SkeletonGrid />
        </Padded>
      ) : active.isError ? (
        <ErrorState
          message={(active.error as Error)?.message}
          onRetry={() => void active.refetch()}
        />
      ) : items.length === 0 ? (
        <Placeholder
          icon="search"
          title={t.noResults}
          description={t.noResultsSub}
          action={
            filtersActive || q
              ? {
                  label: t.clearFilters,
                  onClick: () => {
                    setQ('');
                    // Not `resetFilters()`: that preserves `q` by design, and its closure still
                    // holds the pre-click value, so it would put the query straight back.
                    setParams(new URLSearchParams(), { replace: true });
                  },
                }
              : undefined
          }
        />
      ) : (
        <>
          {/*
            The count has to be the truth or it must not look like a total.

            `items.length` is how many have been *loaded*, so a category holding 1,158 prompts
            announced "24 results" — one page — directly under a chip that said 1,158. Two numbers
            for the same thing, on the same screen, disagreeing by fifty-fold.

            Where the real total is known it is used: a category's size comes from the taxonomy,
            already fetched. Otherwise the loaded count gets a "+" while more pages exist, which
            says "at least this many" rather than "this many" — honest, and it costs no extra
            query. A COUNT over a LIKE scan on every keystroke is not affordable here.
          */}
          <Padded className="pt-3 text-footnote text-hint">
            {t.results(shownCount)}
            {categoryName ? ` · ${categoryName}` : ''}
          </Padded>
          <Padded className="pt-2.5">
            <div className="grid grid-cols-2 gap-2">
              {items.map((card, i) => (
                <div key={card.id} className={i > 11 ? 'cv-auto' : undefined}>
                  <PromptCard
                    card={card}
                    onOpen={openCard}
                    onToggleFavorite={(c) => favMut.mutate(c)}
                    priority={i < 4}
                  />
                </div>
              ))}
            </div>
            <div ref={sentinel} aria-hidden="true" className="h-1" />
            {active.isFetchingNextPage && (
              <div className="pt-2">
                <SkeletonGrid count={2} />
              </div>
            )}
          </Padded>
        </>
      )}

      <div className="h-6" />

      <BottomSheet
        open={sheet}
        onClose={() => setSheet(false)}
        title={t.filters}
        action={
          filtersActive ? (
            <button
              type="button"
              onClick={() => {
                haptic('light');
                resetFilters();
              }}
              className="text-callout text-accent"
            >
              {t.reset}
            </button>
          ) : null
        }
        footer={
          <Button size="lg" className="w-full" onClick={() => setSheet(false)}>
            {t.showResults(shownCount)}
          </Button>
        }
      >
        <SheetOptionGroup
          label={t.type}
          options={[
            { value: 'all', label: t.all, selected: !type, onSelect: () => setParam('type') },
            /*
              From the taxonomy, not a hardcoded list.

              The fixed six offered `code` and `audio` — **zero** prompts each — and omitted `3d`
              and `design`, which have content. A hand-written filter list drifts from the library
              the moment ingestion changes, and every entry that drifts is a dead end.
            */
            ...(taxonomy?.types ?? []).map((ty) => ({
              value: ty.type,
              label: withCount(promptTypeLabel(ty.type, lang), ty.count, lang),
              selected: type === ty.type,
              onSelect: () => setParam('type', ty.type),
            })),
          ]}
        />
        <SheetOptionGroup
          label={t.model}
          options={[
            { value: 'all', label: t.all, selected: !model, onSelect: () => setParam('model') },
            ...(taxonomy?.models ?? []).map((m) => ({
              value: m.slug,
              label: withCount(m.name, m.count, lang),
              selected: model === m.slug,
              onSelect: () => setParam('model', m.slug),
            })),
          ]}
        />
        <SheetOptionGroup
          label={t.category}
          options={[
            {
              value: 'all',
              label: t.all,
              selected: !category,
              onSelect: () => setParam('category'),
            },
            ...(taxonomy?.categories ?? []).map((c) => ({
              value: c.slug,
              label: withCount(c.name, c.count, lang),
              selected: category === c.slug,
              onSelect: () => setParam('category', c.slug),
            })),
            /*
              A category filtered in by URL but too small to be listed still gets a row.
              The taxonomy omits categories under the browse threshold, and an old link to one
              would otherwise leave the sheet showing "All" as selected while a filter was very
              much applied — the UI contradicting the results.
            */
            ...(category && !(taxonomy?.categories ?? []).some((c) => c.slug === category)
              ? [
                  {
                    value: category,
                    label: category,
                    selected: true,
                    onSelect: () => setParam('category', category),
                  },
                ]
              : []),
          ]}
        />
        <SheetOptionGroup
          label={t.sourceMeta}
          options={[
            {
              value: '',
              label: t.allSources,
              selected: !platform,
              onSelect: () => setParam('platform', undefined),
            },
            ...SOURCE_PLATFORMS.map((p) => ({
              value: p,
              // The label a card shows, so a filter chip and the cards it produces agree.
              label: PLATFORM_LABELS[p],
              selected: platform === p,
              onSelect: () => setParam('platform', p),
            })),
          ]}
        />
        <SheetOptionGroup
          label={t.sort}
          options={SORTS.map((s) => ({
            value: s,
            label: sortLabels[s],
            selected: sort === s,
            onSelect: () => setParam('sort', s === 'trending' ? undefined : s),
          }))}
        />
      </BottomSheet>
    </Screen>
  );
}
